<template>
  <main id="skip-to-main-content" class="request-show" :key="rerenderKey">
    <VisibilityBanner
      v-if="
        $can('update', 'Request') ||
        (isCurrentRequester && request.request_visibility != 'published')
      "
      :account="$store.state.account"
      :request-visibility="request.request_visibility"
      :department-names="request.department_names"
    />
    <div
      v-if="$can('read', 'staff_only_items') && legacyRequestPageShowEnabled"
      class="container block preview-switch-container"
    >
      <NrButton
        class="preview-switch"
        variant="bordered-neutral"
        @click="openSwitchToPreviewModal"
      >
        Switch to legacy page
      </NrButton>
    </div>
    <NrModal
      title="Return to the legacy request page?"
      primaryButtonText="Switch to legacy page"
      secondaryButtonText="Cancel"
      :hasSecondaryButton="true"
      :show="currentAction === 'preview-modal'"
      @on-close-click="setCurrentAction(null)"
      @on-secondary-click="setCurrentAction(null)"
      @on-primary-click="goToPreview"
    >
      <p>
        By switching back to the legacy request page you will be able to view
        the same request data but in an older format. You may experience
        degraded performance and some new features, such as additional
        information from custom fields, will not be available. This will only
        affect your user account, and you can return to the new request page at
        any time.
      </p>
    </NrModal>
    <RequestPaymentsModal
      :show="activeModal === 'requestPaymentsDialog'"
      :invoice="firstOutstandingInvoice"
    />
    <div class="container block no-margin-bottom">
      <RequestShowHeader>
        <template v-slot:id>
          <h1 class="is-size-3 has-text-black has-text-weight-bold">
            Request {{ prettyId }}
          </h1>
        </template>
        <template v-slot:banner>
          <NrBanner :type="setRequestStatusText()" v-if="requestPresent" />
          <span v-else class="banner-tray">
            <NrLoadingAnimation :count="1" />
          </span>
        </template>
        <template
          v-slot:invoiceNotice
          v-if="
            isCurrentRequester &&
            anyOutstandingInvoice &&
            $store.state.account.invoicing_feature_flag
          "
        >
          <span class="payment-notice">
            <div class="payment-notice-explanation">
              <NrIcon class="large-icon inline-icon" name="info-circle" />
              <p>
                An invoice has been sent, please pay in order to proceed with
                the process.
              </p>
            </div>
            <NrButton
              class="no-wrap payment-notice-button"
              variant="bordered-primary"
              aria-label="Pay invoice"
              @click="setActiveModal('requestPaymentsDialog')"
            >
              <NrIcon class="inline-icon" name="dollar-sign" /> Pay invoice
            </NrButton>
          </span>
        </template>
        <template v-slot:icons>
          <template v-if="requestPresent">
            <span v-for="icon in icons" :key="`nr-${icon.name}-${icon.label}`">
              <NrTooltip :label="icon.label">
                <span>
                  <NrIconButton
                    size="large"
                    variant="minimal-neutral"
                    :icon="{ name: icon.name, type: 'regular' }"
                    :aria-label="icon.label"
                    :class="`action-buttons action-buttons-${icon.name}`"
                    :disabled="iconDisabled(icon.name)"
                    @click="handleActions(icon.name)"
                  />
                </span>
              </NrTooltip>
            </span>
          </template>
          <NrLoadingAnimation v-else :count="1" />
        </template>
        <template v-if="cyclingEnabled" v-slot:cycling>
          <RequestShowCycleArrows :prettyId="prettyId" />
        </template>
        <template v-if="cyclingEnabled" v-slot:filters>
          <NrTooltip :label="cycleTagTooltip">
            <NrTag
              :label="cycleTagText"
              size="normal"
              tagColor="purple"
              badge
            />
          </NrTooltip>
        </template>
      </RequestShowHeader>
    </div>

    <div class="container block">
      <div class="columns mobile-order">
        <div class="column is-4-desktop mobile-order-2">
          <RequestDates
            :prettyId="this.prettyId"
            :dueDate="request.request_due_date"
            :received="request.request_date"
            :fulfillment="request.anticipated_fulfilled_at"
            :expiration="request.expiration_date"
            :exempt-from-retention="request.exempt_from_retention"
            :request-state="request.request_state"
            :request-submit-type="request.request_submit_type"
            :can-see-request-due-date="
              $can('read', 'staff_only_items') ||
              $store.state.account.show_due_date
            "
            :can-see-request-expiration-date="
              $can('read', 'staff_only_items') &&
              $store.state.account.retention &&
              request.request_state === 'Closed'
            "
          />
          <RequesterInfo
            :prettyId="prettyId"
            :name="request.requester && request.requester.name"
            :email="request.requester && request.requester.email"
            :address="request.requester && request.requester.address"
            :company="request.requester && request.requester.company"
            :phone="request.requester && request.requester.phone"
            :requester-id="request.requester && request.requester.id"
            :can-see-requester-panel="
              $can('read', 'staff_only_items') ||
              isCurrentRequester ||
              $store.state.account.show_requester
            "
            :can-see-requester-details="
              $can('read', 'staff_only_items') || isCurrentRequester
            "
            :can-see-email-statuses="$can('read', 'staff_only_items')"
          />
          <RequestFieldValues
            v-if="!isEmpty(requestFieldValues)"
            :fields="requestFieldValues"
          />
          <CustomFields
            v-if="!isEmpty(request.custom_fields)"
            :fields="request.custom_fields"
            :prettyId="prettyId"
          />
          <RequestPayments
            v-if="
              accountPaymentsEnabled &&
              (canSeeStaffOnlyItems || isCurrentRequester)
            "
            :request-invoices="requestInvoices"
          />
          <RequestStaffHours
            v-if="canSeeRequestStaffHours"
            :staff-cost="request.request_staff_cost"
            :total-staff-hours="request.request_staff_hours"
            @set-staff-time-for-edit="setSelectedStaffHour($event)"
          />
          <RequestAssigned
            :account="account"
            :request-id="request.pretty_id"
            :account-departments="account.departments"
            :departments="request.departments"
            :prettyId="this.prettyId"
            :visibility="request.visibility"
            :poc="request.poc"
            :requestStaff="requestStaff"
            :staffLimit="staffLimit"
          />
          <RequestStaffTime
            v-if="canSeeStaffOnlyItems"
            v-on:reset-staff-time="setSelectedStaffHour({})"
            :staff-time="selectedStaffHour"
          />
          <RequestTags
            v-if="$can('update', 'Request')"
            :requestId="request.pretty_id"
            :tags="requestTags"
          />
        </div>
        <div class="column is-8-desktop mobile-order-1">
          <!-- request_text is sanitized here: https://github.com/nextrequest/nextrequest/blob/83b5a5/app/views/client/requests/show.json.jbuilder#L3 -->
          <RequestInfoHeader :sanitizedDescription="request.request_text" />
          <RequestPinnedNote />
          <RequestTabs
            :timeline="requestTimeline"
            :documents="documents"
            :documentsFolders="documentsFolders"
            :tasks="requestTasks"
            @tab-change="loadTabData"
            @handle-timeline-filters="handleTimelineFilters"
            @handle-tasks-status-filters="handleTasksStatusFilters"
          />
        </div>
      </div>
    </div>

    <Footer :account="$store.state.account" />

    <MessageBoxes />

    <DocumentUploadModal
      :show="currentAction === 'paperclip'"
      :enable-folder-assignment="canSeeStaffOnlyItems"
      :enable-doc-visibility-assignment="canSeePublisherOnlyItems"
      :enable-links-tab="canSeeStaffOnlyItems"
      :is-modal-active="currentAction === 'paperclip'"
      :account="$store.state.account"
      :folders="folders"
      :folder="folder"
      :clearableVisibilityOption="false"
      @search-folder="handleFolderSearch"
      @selected-folder="handleFolderSelection"
      @create-folder="handleFolderCreation"
      @on-message-template-search="searchMessageTemplates"
      @on-message-template-scroll="loadMoreMessageTemplates"
      :messageTemplateState="dataFetchState.message_templates"
      :request-id="prettyId"
      :s3-bucket="s3Bucket"
      :evaporate-response="evaporateResponse"
      :uploaded-docs="uploadedDocs"
      :message-templates="filteredMessageTemplates"
      @on-file-upload-complete="addUploadedDocToState"
      @on-submit="saveDocFiles"
      @on-close-modal="
        () => {
          folder = null
          setCurrentAction(null)
        }
      "
    />

    <NrModal
      size="small"
      title="Reopen request"
      primaryButtonText="Reopen request"
      secondaryButtonText="Cancel"
      :hasSecondaryButton="true"
      :show="currentAction === 'folder-open'"
      :disableButtons="reopening"
      @on-close-click="setCurrentAction(null)"
      @on-secondary-click="setCurrentAction(null)"
      @on-primary-click="handleReopen"
      ref="reopen-modal"
    >
      <p>You are about to reopen this request. Do you want to continue?</p>
    </NrModal>
    <NrModal
      :show="currentAction === 'pause'"
      :hasSecondaryButton="true"
      :disableButtons="pausing"
      primaryButtonText="Pause request"
      secondaryButtonText="Cancel"
      title="Pause request"
      @on-close-click="handlePausing(false)"
      @on-secondary-click="handlePausing(false)"
      @on-primary-click="handlePausing(true)"
    >
      <p>
        If you are waiting for the requester and no longer want to receive
        overdue/due soon notices, pause the request. The request will be
        automatically resumed if the requester takes action (posts a new
        message, uploads a document, or pays an invoice).
      </p>
      <br />
      <NrFormLabel>Comments (posts to staff timeline only)</NrFormLabel>
      <NrEditor
        autofocus
        bold
        bulletList
        italic
        link
        orderedList
        placeholder="Example: Waiting for a requester to provide specific dates and times of their request."
        underline
        v-model="pauseComment"
      />
    </NrModal>
    <close-request-modal
      v-if="canSeePublisherOnlyItems"
      :active="currentAction === 'check-square'"
      @on-close="setCurrentAction(null)"
      @on-submit="submitCloseRequest"
    />
  </main>
</template>
<script>
import { throttle, isEmpty, truncate, uniqBy } from 'lodash'
import VisibilityBanner from './VisibilityBanner.vue'
import RequestShowHeader from './RequestShowHeader.vue'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements/nr-form-label.vue'
import Footer from '../../../vue_shared/components/Footer.vue'
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex'
import RequestDates from './RequestDates.vue'
import RequesterInfo from './RequesterInfo.vue'
import RequestPayments from './RequestPayments.vue'
import RequestStaffHours from './request-staff-hours.vue'
import RequestAssigned from './RequestAssigned.vue'
import RequestStaffTime from './RequestStaffTime.vue'
import RequestTabs from './RequestTabs.vue'
import RequestTags from './RequestTags.vue'
import RequestInfoHeader from './RequestInfoHeader.vue'
import RequestPinnedNote from './RequestPinnedNote.vue'
import MessageBoxes from './MessageBoxes'
import CustomFields from './CustomFields.vue'
import RequestFieldValues from './RequestFieldValues.vue'
import NrBanner from '@nextrequestco/components/src/components/NrBanner/NrBanner.vue'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip/NrTooltip.vue'
import DocumentUploadModal from '@nextrequestco/components/src/components/DocumentUploadModal/DocumentUploadModal.vue'
import NrModal from '@nextrequestco/components/src/components/NrModal/nr-modal.vue'
import NrEditor from '@nextrequestco/components/src/components/NrEditor/nr-editor.vue'
import NrButton from '@nextrequestco/components/src/components/NrButton/nr-button.vue'
import NrIcon from '@nextrequestco/components/src/components/NrIcon/NrIcon.vue'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton/nr-icon-button.vue'
import NrTag from '@nextrequestco/components/src/components/NrTag/NrTag.vue'
import CloseRequestModal from './CloseRequestModal.vue'
import RequestPaymentsModal from './RequestPaymentsModal'
import setupEvaporateFileUploader from '../../../mixins/init_evaporate.js'
import API from '../../../vue_shared/api'
import RequestShowCycleArrows from './RequestShowCycleArrows.vue'
import NrLoadingAnimation from '@nextrequestco/components/src/components/NrLoadingAnimation/nr-loading-animation.vue'
import NrSentry from '../../../utils/nr_sentry'

export default {
  name: 'RequestShowPage',
  props: {
    prettyId: {
      required: true
    }
  },

  components: {
    NrModal,
    NrEditor,
    NrTooltip,
    NrBanner,
    NrButton,
    NrIcon,
    NrIconButton,
    NrFormLabel,
    NrTag,
    NrLoadingAnimation,
    VisibilityBanner,
    Footer,
    RequestDates,
    RequesterInfo,
    RequestPayments,
    RequestStaffHours,
    RequestStaffTime,
    RequestAssigned,
    RequestTabs,
    RequestTags,
    RequestShowHeader,
    RequestInfoHeader,
    RequestPaymentsModal,
    RequestPinnedNote,
    MessageBoxes,
    CustomFields,
    DocumentUploadModal,
    CloseRequestModal,
    RequestShowCycleArrows,
    RequestFieldValues
  },

  mixins: [setupEvaporateFileUploader],
  mounted() {
    this.reinitializeState()
    this.rerender()
    this.setupRequestShowPageData({
      ability: this.$ability,
      prettyId: this.prettyId
    })
    this.fetchTimeline({ pretty_id: this.prettyId, filter_categories: [] })
    this.setupEvaporateFileUploader()
    this.s3Bucket = process.env.S3_BUCKET
  },

  beforeRouteEnter(to, _from, next) {
    next((vm) => {
      // access to component public instance via `vm`
      vm.setupToasts(vm, to)
    })
  },

  watch: {
    $route(prev, next) {
      if (prev.name !== next.name) {
        return
      }
      if (prev.params.prettyId === next.params.prettyId) {
        return
      }
      this.reinitializeState()
      this.rerender()
      this.setupRequestShowPageData({
        ability: this.$ability,
        prettyId: this.prettyId
      })
    },
    prettyId(newVal) {
      this.fetchTimeline({ pretty_id: newVal, filter_categories: [] })
      this.fetchDocuments({ request_id: newVal })
      this.fetchDocumentsFolders({ request_id: newVal })
      if (this.canSeeStaffOnlyItems) {
        this.fetchRequestTasks({
          pretty_id: newVal,
          params: { user_id: null, task_status: null, page_number: 1 }
        })
      }
      this.setCurrentAction('')
    },

    previewRequestPage() {
      if (!this.previewRequestPage) {
        window.location.reload()
      }
    },
    currentAction(next, prev) {
      this.uploadedDocs = []
      if (!next || next === prev) {
        return
      }
      if (next === 'paperclip') {
        if (!this.documentsFolders.length) {
          this.fetchDocumentsFolders({ request_id: this.prettyId })
        }
      }
    }
  },

  data() {
    return {
      reopening: false,
      pausing: false,
      checked: false,
      folder: null,
      externalMessageRequired: false,
      staffLimit: 20,
      previewOn: true,
      uploadedDocs: [],
      evaporateResponse: {},
      s3Bucket: '',
      docUploadFolders: [],
      pauseComment: '',
      rerenderKey: 0
    }
  },

  computed: {
    ...mapState('request_show', [
      'activeModal',
      'request',
      'documents',
      'documentsFolders',
      'requestStaff',
      'requestTags',
      'requestTasks',
      'requestTimeline',
      'requestInvoices',
      'currentDocumentFilters',
      'previewRequestPage',
      'currentAction',
      'dataFetchState',
      'dataOptions',
      'filteredRequestLoading',
      'selectedStaffHour'
    ]),

    ...mapState('requests', ['requests', 'filters', 'tableHeaderTitleTooltip']),
    ...mapGetters('requests', ['activeFilters']),

    ...mapState('request_show', {
      folders(state) {
        return uniqBy(
          state.documentsFolders.map((folder) => ({
            label: folder.folder_name,
            value: folder.id,
            children: folder.has_child_folders
          })),
          'value'
        )
      }
    }),

    anyOutstandingInvoice() {
      return this.requestInvoices.some((invoice) => !!invoice.outstanding)
    },

    firstOutstandingInvoice() {
      return this.requestInvoices.find((invoice) => !!invoice.outstanding)
    },

    legacyRequestPageShowEnabled() {
      return this.account.legacy_request_show_enabled
    },

    ...mapState(['account', 'currentUser']),
    canSeeRequestStaffHours() {
      return (
        this.request.request_staff_hours &&
        this.request.request_staff_hours.length &&
        this.$ability.can('read', 'staff_only_items')
      )
    },
    canSeePublisherOnlyItems() {
      return this.$ability.can('read', 'publisher_only_items')
    },
    canSeeStaffOnlyItems() {
      return this.$ability.can('read', 'staff_only_items')
    },
    canUpdateRequest() {
      return this.$ability.can('update', 'Request')
    },
    departmentNames() {
      if (this.request.department_names !== 'None assigned') {
        return this.request.department_names.split(', ')
      }
      return ''
    },

    currentUserPresent() {
      return isEmpty(this.currentUser) !== true
    },

    requestPresent() {
      return isEmpty(this.request) !== true
    },

    canSeeExternalMessageIcon() {
      return (
        this.canSeePublisherOnlyItems ||
        (this.currentUserPresent && this.isCurrentRequester)
      )
    },

    canSeeNoteMessageIcon() {
      return (
        this.canSeeStaffOnlyItems &&
        ['wsu', 'albanycounty'].indexOf(this.$store.state.account.subdomain)
      )
    },

    canSeeInvoiceIcon() {
      const adminOrPublisher =
        this.$ability.can('publish', 'Request') ||
        this.$ability.can('manage', 'all')

      return this.account && this.account.payments && adminOrPublisher
    },

    accountPaymentsEnabled() {
      return this.account.payments
    },

    isCurrentRequester() {
      if (this.request.is_current_requester) {
        return true
      }

      return this.currentUser?.id === this.request?.requester?.id
    },

    canSeeUploadIcon() {
      return (
        this.currentUserPresent &&
        (this.canSeeStaffOnlyItems || this.isCurrentRequester)
      )
    },

    canManageRequest() {
      return this.$ability.can('manage', 'Request')
    },

    canSeeUnpauseIcon() {
      return (
        this.canManageRequest &&
        this.request.request_state !== 'Closed' &&
        this.request.request_state === 'Waiting for requester'
      )
    },

    canSeePauseIcon() {
      return (
        this.canManageRequest &&
        !['Waiting for requester', 'Closed'].includes(
          this.request.request_state
        )
      )
    },

    canSeeCloseIcon() {
      return this.canManageRequest && this.request.request_state !== 'Closed'
    },

    canSeeReopenIcon() {
      return this.canManageRequest && this.request.request_state === 'Closed'
    },

    canSeeRapidReviewIcon() {
      return (
        this.$store.state.account &&
        this.$store.state.account.document_review &&
        this.$store.state.account.redaction &&
        this.canSeeStaffOnlyItems
      )
    },

    currentRequestIndex() {
      return this.requests.findIndex(
        (request) => String(this.prettyId) === String(request.id)
      )
    },

    cyclingEnabled() {
      return this.filters.page_number === 1 && this.currentRequestIndex >= 0
    },

    cycleTagTooltip() {
      if (this.cyclingEnabled) {
        return this.tableHeaderTitleTooltip
      } else {
        return 'Only 100 requests can by cycled at one time. Adjust filters to narrow the scope of your search.'
      }
    },

    cycleTagText() {
      if (this.cyclingEnabled) {
        return `${this.currentRequestIndex + 1} of ${
          this.requests.length > 99 ? 'many' : this.requests.length
        }${this.activeFilters.length > 0 ? ' with filters active' : ''}`
      } else {
        return 'request cycling not active'
      }
    },

    icons() {
      return [
        { name: 'tools', label: 'Launch RapidReview' },
        {
          name: 'envelope',
          label: this.isCurrentRequester
            ? 'Message agency'
            : 'Message requester'
        },
        { name: 'comments', label: 'Message staff' },
        { name: 'edit', label: 'Add note' },
        { name: 'clock', label: 'Add staff time' },
        {
          name: 'dollar-sign',
          label: this.anyOutstandingInvoice
            ? 'Invoice already sent, awaiting payment'
            : 'Add invoice'
        },
        {
          name: 'paperclip',
          label: this.iconDisabled('paperclip')
            ? "Can't upload to closed request"
            : 'Add document'
        },
        { name: 'pause', label: 'Pause request' },
        { name: 'play', label: 'Resume request' },
        { name: 'folder-open', label: 'Reopen request' },
        { name: 'check-square', label: 'Close request' },
        { name: 'tasks', label: 'Add task' },
        { name: 'print', label: this.getPrintButtonLabel() }
      ].filter((icon) => this.iconVisible(icon.name))
    },
    filteredMessageTemplates() {
      return this.dataOptions.message_templates.map((item) => ({
        value: item.id,
        label: item.message_template_name,
        description: item.message_template_description
      }))
    },
    requestFieldValues() {
      return this.request.request_field_values
    }
  },

  methods: {
    isEmpty,
    ...mapActions('requests', ['fetchRequests']),

    ...mapActions('request_show', [
      'addDocument',
      'addFolder',
      'closeRequest',
      'fetchDocuments',
      'fetchDocumentsFolders',
      'fetchDataOptions',
      'fetchRequestTags',
      'fetchRequestTasks',
      'fetchTimeline',
      'pollBulkJobs',
      'pause',
      'reopen',
      'resume',
      'reinitializeState',
      'setActiveModal',
      'setCurrentAction',
      'setDisplayCreateInvoiceMsgBox',
      'setDisplayCreateStaffTimeBox',
      'setDisplayCreateTaskMsgBox',
      'setDisplayExternalMsgBox',
      'setDisplayInternalMsgBox',
      'setDisplayMsgBox',
      'setupRequestShowPageData',
      'submitExternalMessage',
      'submitInternalMessage',
      'switchToOldPage',
      'updateRequest'
    ]),
    ...mapActions('documents', ['saveDocs', 'createFolder']),
    ...mapMutations('request_show', ['setSelectedStaffHour']),

    async setupToasts(vm, to) {
      const query = to.query
      let clearQuery = false
      if (query?.toast_info) {
        vm.$toasted.show(query.toast_info)
        clearQuery = true
      }
      if (query?.toast_error) {
        vm.$toasted.error(query.toast_error)
        clearQuery = true
      }
      if (query?.toast_success) {
        vm.$toasted.success(query.toast_success)
        clearQuery = true
      }
      if (clearQuery) {
        // Clear the query params so as to not show the toasts again on refresh, or if the URL is copied.
        vm.$router.replace(`/requests/${vm.prettyId}`)
      }
    },

    openSwitchToPreviewModal() {
      this.setCurrentAction('preview-modal')
    },

    searchMessageTemplates: throttle(function (searchText = '') {
      this.fetchDataOptions({ searchText, dataType: 'message_templates' })
    }, 400),

    loadMoreMessageTemplates: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.message_templates.search_text,
        dataType: 'message_templates'
      })
    }, 400),

    rerender() {
      this.rerenderKey += 1
    },

    submitCloseRequest(params) {
      this.closeRequest({
        prettyId: this.request.pretty_id,
        description: params.close_description,
        reasons: params.close_reasons,
        state: params.state
      }).then(() => {
        this.fetchTimeline({ pretty_id: this.prettyId, filter_categories: [] })
        if (
          params.visibility &&
          params.visibility !== this.request.visibility
        ) {
          this.updateRequest({
            request_id: this.request.pretty_id,
            visibility: params.visibility
          }).catch((e) => {
            this.$toasted.error('There was an error updating the request')
            NrSentry.captureError(e)
          })
        }
        this.setCurrentAction(null)
      })
    },
    navigateToRapidReview() {
      this.$router.push({ path: `/app/rapidreview/${this.prettyId}` })
    },
    addUploadedDocToState(val) {
      this.uploadedDocs.push(val)
    },
    saveDocFiles(params) {
      if (this.isCurrentRequester) {
        Object.assign(params, { state: 'requester' })
      }

      API.saveDocs(params)
        .then((response) => {
          if (!response.data.documents) return
          response.data.documents.forEach(this.addDocument)
          this.$toasted.success('Successfully uploaded document(s)')
          this.fetchTimeline({
            pretty_id: this.prettyId,
            filter_categories: []
          })
          this.fetchDocuments({ request_id: this.prettyId })
          this.fetchDocumentsFolders({ request_id: this.prettyId })
        })
        .catch((err) => {
          if (err.response?.status === 403) {
            this.$toasted.error(
              'You do not have permission to upload documents.'
            )
          } else {
            NrSentry.captureError(
              new Error('Documents upload failed', { cause: err })
            )
            this.$toasted.error('An error occured. Please try again.')
          }
        })
    },
    handleFolderSearch: throttle(function (val = '') {
      const payload = { request_id: this.prettyId }

      if (val) {
        payload.search_text = val
      }

      API.fetchRequestDocumentsFolders(payload).then((response) => {
        const { folders } = response.data
        this.docUploadFolders = folders
      })
    }, 400),

    async handleFolderCreation(folder) {
      // Removing the folder association to this document pre-submission
      if (!folder) {
        this.folder = null
        return
      }

      // This indicates that we are creating a new folder
      if (typeof folder === 'string' || !folder.value) {
        this.folder = 'pending'
        try {
          const newFolder = await this.createFolder(
            typeof folder === 'object' ? folder.label : folder
          )
          this.handleFolderSelection({
            value: newFolder.id,
            label: newFolder.folder_name
          })
          this.addFolder(this.folder)
        } catch (_err) {
          this.folder = null
        }
      }
    },

    handleFolderSelection(folder) {
      // Removing the folder association to this document pre-submission
      if (!folder) {
        this.folder = null
        return
      }

      // Associate a folder to this document
      this.folder = folder
    },
    // eslint-disable-next-line camelcase
    handleTimelineFilters({ filters, page_number }) {
      const categories = filters.map((item) => item.id)
      this.$store.commit(
        'request_show/setTimelineFiltersApplied',
        categories.length > 0
      )
      if (categories.includes('all timeline events')) {
        this.fetchTimeline({
          pretty_id: this.prettyId,
          filter_categories: [
            'messages',
            'notes',
            'documents',
            'status',
            'due date',
            'tasks',
            'staff',
            'visibility',
            'tags',
            'invoices',
            'hours'
          ],
          page_number
        })
      } else {
        this.fetchTimeline({
          pretty_id: this.prettyId,
          filter_categories: categories,
          page_number
        })
      }
    },

    handleTasksStatusFilters(val) {
      this.fetchRequestTasks({
        pretty_id: this.prettyId,
        params: {
          user_id: val.assignee,
          task_status: val.type,
          page_number: val.pageNumber
        }
      })
    },

    handleActions(val) {
      switch (val) {
        case 'dollar-sign':
          if (this.anyOutstandingInvoice) {
            break
          } else {
            this.setCurrentAction(val)
            this.setDisplayCreateInvoiceMsgBox(true)
            break
          }
        case 'clock':
          this.setActiveModal(val)
          this.setDisplayCreateStaffTimeBox(true)
          break
        case 'envelope':
          this.setCurrentAction(val)
          this.setDisplayCreateTaskMsgBox({ display: false })
          this.setDisplayMsgBox(true)
          break
        case 'comments':
          this.setCurrentAction(val)
          this.setDisplayCreateTaskMsgBox({ display: false })
          this.setDisplayMsgBox(true)
          break
        case 'edit':
          this.setCurrentAction(val)
          this.setDisplayCreateTaskMsgBox({ display: false })
          this.setDisplayMsgBox(true)
          break
        case 'tasks':
          this.setCurrentAction(val)
          this.setDisplayInternalMsgBox(false)
          this.setDisplayExternalMsgBox(false)
          this.setDisplayCreateTaskMsgBox({ display: true, type: 'new' })
          break
        case 'tools':
          this.navigateToRapidReview()
          break
        case 'play':
          this.handleResume()
          break
        case 'pause':
          this.setCurrentAction(val)
          break
        case 'paperclip':
          if (this.iconDisabled(val)) {
            break
          }
          this.setCurrentAction(val)
          break
        case 'folder-open':
          this.setCurrentAction(val)
          break
        case 'check-square':
          this.setCurrentAction(val)
          break
        case 'print':
          this.handlePrint()
          break
        default:
      }
    },

    setupRequestDescription() {
      return truncate(this.request.request_text, { length: '700' })
    },
    goToPreview() {
      this.switchToOldPage()
    },
    loadTabData(activeTabId) {
      // Loads data on tab click
      if (activeTabId === 'documents-tab') {
        // If no documents, folders and filters present, fetch all documents
        if (isEmpty(this.currentDocumentFilters)) {
          this.fetchDocuments({ request_id: this.prettyId })
          this.fetchDocumentsFolders({ request_id: this.prettyId })
        } else {
          this.fetchDocuments(this.currentDocumentFilters)
        }
        return
      }
      if (activeTabId === 'tasks-tab') {
        this.fetchRequestTasks({
          pretty_id: this.prettyId,
          params: { user_id: null, task_status: null, page_number: 1 }
        })
      }
    },
    setRequestStatusText() {
      switch (this.request.request_state) {
        case 'Waiting for requester':
          return 'Paused'
        default:
          return this.request.request_state
      }
    },

    iconVisible(icon) {
      switch (icon) {
        case 'envelope':
          return this.canSeeExternalMessageIcon
        case 'comments':
          return this.canSeeStaffOnlyItems
        case 'tasks':
          return this.canSeeStaffOnlyItems
        case 'edit':
          return this.canSeeNoteMessageIcon
        case 'dollar-sign':
          return this.canSeeInvoiceIcon
        case 'clock':
          return this.canSeeStaffOnlyItems
        case 'paperclip':
          return this.canSeeUploadIcon
        case 'play':
          return this.canSeeUnpauseIcon
        case 'pause':
          return this.canSeePauseIcon
        case 'check-square':
          return this.canSeeCloseIcon
        case 'folder-open':
          return this.canSeeReopenIcon
        case 'tools':
          return this.canSeeRapidReviewIcon
        case 'print':
          return this.canSeeStaffOnlyItems
      }
    },

    iconDisabled(icon) {
      return (
        icon === 'paperclip' &&
        this.iconVisible(icon) &&
        this.isCurrentRequester &&
        this.request.request_state === 'Closed'
      )
    },

    handleReopen() {
      this.reopening = true
      this.reopen(this.request.pretty_id).finally(() => {
        this.reopening = false
        this.setCurrentAction(null)
      })
    },

    async handlePausing(persist = false) {
      this.pausing = true

      if (persist) {
        try {
          await this.pause({
            prettyId: this.request.pretty_id,
            description: this.pauseComment
          })

          this.$toasted.success('Request paused successfully')
        } catch (error) {
          this.$toasted.error(
            'Request failed to pause. Please refresh the page and try again.'
          )
        }
      }

      this.pausing = false
      this.pauseComment = ''
      this.setCurrentAction(null)
    },

    handleResume() {
      this.resume(this.request.pretty_id)
    },

    handlePrint() {
      let url = `/requests/${this.request.pretty_id}.pdf`

      if (
        this.$store.state.request_show.timelineFiltersApplied &&
        this.requestTimeline.length > 0
      ) {
        const timelineIds = this.requestTimeline
          .map((item) => item.timeline_id)
          .join(',')
        url += `?timeline_ids=${timelineIds}`
      }

      window.open(url, '_blank')
    },
    getPrintButtonLabel() {
      return this.$store.state.request_show.timelineFiltersApplied
        ? 'Print Filtered Request'
        : 'Print Request'
    }
  }
}
</script>
<style lang="scss">
.preview-switch-container {
  display: flex;
  justify-content: flex-end;
}

.panel-heading {
  display: flex;
  align-items: center;
  min-height: 3.25rem;
  padding-left: 0.75rem;
  padding-top: 0;
  padding-bottom: 0;
  margin-top: 12px;
}

.panel {
  border-radius: 4px;
  box-shadow:
    0 0 0 1px rgba(10, 10, 10, 0.1),
    0 1px 16px -0.1px rgba(10, 10, 10, 0.1);
}

.panel-content {
  padding: 1rem 0.75rem;
}

.is-align-items-center {
  align-items: center;
}
.is-align-items-flex-start {
  align-items: flex-start;
}
.is-justify-content-space-between {
  justify-content: space-between;
}

.panel-section {
  .panel-item {
    padding: 0.375rem 0.5rem;
  }
  .panel-item--editable {
    margin: 0 -0.75rem;
    padding: 0 0.75rem;
    &:hover {
      background-color: #ebebef;
    }
  }
}

/* Adds extra margin space for screen sizes 1920 or larger */
@media screen and (min-width: 1920px) {
  .request-show .container.is-fluid {
    margin-left: 292px !important;
    margin-right: 292px !important;
  }
}
/* Aligns show more button to the right without using float: right */
.request-show-more-btn {
  display: flex;
  justify-content: flex-end;
}

.request-show {
  /**
   * Override modal min-height. TODO: Remove this rule from the modal.
   */
  .nr-modal__container {
    min-height: unset !important;
  }
  .no-margin-bottom {
    margin-bottom: 0px;
  }
}

.banner-tray {
  min-width: 8em;
}

.payment-notice {
  display: flex;
  align-items: center;
  padding: 8px;
  flex-wrap: wrap;
}

.payment-notice-explanation {
  display: flex;
  align-items: center;
  padding: 4px;
}

.payment-notice p {
  margin: 0 0.5em;
  min-width: 10em;
}

.payment-notice-button {
  flex-grow: 1;
}

.inline-icon {
  display: inline-block;
  vertical-align: middle;
}

.large-icon {
  min-width: 1.5em;
}

.no-wrap {
  text-wrap: nowrap;
}
</style>
